import { Link } from "@inertiajs/react"
import { LegalPage } from "@/components/LegalPage"

type Props = { last_updated: string; contact_email: string }

const title = "Privacy Policy"
const description =
  "How the Australian Music Database handles your personal information — what we collect, why, who we share it with, and how to access or delete it."

export default function Privacy({ last_updated, contact_email }: Props) {
  return (
    <LegalPage title={title} description={description} lastUpdated={last_updated}>
      <section>
        <p>
          The Australian Music Database (australianmusicdatabase.com) is a research archive of Australian
          music history, run by Mark Gibson. This policy explains what personal information the site
          collects, why it is collected, and what you can do about it.
        </p>
        <p>
          The site is operated from Australia and handles personal information in line with the{" "}
          <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles.
        </p>
      </section>

      <section>
        <h2>What we collect</h2>
        <p>Most of the site can be browsed without an account and without giving us anything. We collect:</p>
        <ul>
          <li>
            <strong>Account details</strong> — if you create an account, your name and email address.
            Passwords are stored only as a salted hash, never in a form we or anyone else can read.
          </li>
          <li>
            <strong>Contributions</strong> — records you add or edit are attributed to your account, so
            we know who contributed what and can credit it.
          </li>
          <li>
            <strong>Donations</strong> — if you donate, we store the amount, a payment reference, which
            ad-free tier it granted, when that expires, and whether you chose to appear publicly on the
            Wall of Fame. <strong>We never see or store your card details</strong> — payment is handled
            entirely by Stripe.
          </li>
          <li>
            <strong>Quiz scores</strong> — if you play the trivia quiz while signed in, your score is
            saved to your account. Appearing on the public leaderboard is opt-in.
          </li>
          <li>
            <strong>Technical data</strong> — standard web server logs, which include IP address,
            browser type and the pages requested. These are used to keep the site running and secure.
          </li>
        </ul>
      </section>

      <section>
        <h2>Cookies</h2>
        <p>
          A single essential cookie keeps you signed in between visits. It is required for accounts to
          work at all and cannot be turned off while you are signed in.
        </p>
        <p>
          When advertising is enabled, Google AdSense may also set its own cookies to measure and
          personalise ads. These are Google's cookies, governed by Google's own privacy policy, not
          ours. Donors on an ad-free tier are served no ads and therefore no advertising cookies.
        </p>
      </section>

      <section>
        <h2>Who we share it with</h2>
        <p>
          We do not sell your personal information, and we do not share it for anyone else's marketing.
          We rely on a small number of service providers to run the site:
        </p>
        <ul>
          <li><strong>Heroku</strong> — hosting and the database.</li>
          <li><strong>Stripe</strong> — donation payment processing.</li>
          <li><strong>Cloudinary</strong> — image hosting and delivery.</li>
          <li><strong>Google AdSense</strong> — advertising, when enabled.</li>
          <li><strong>Email delivery</strong> — for account emails such as verification and password resets.</li>
        </ul>
        <p>
          Some of these providers operate servers outside Australia, which means your information may be
          stored or processed overseas.
        </p>
      </section>

      <section>
        <h2>Public information</h2>
        <p>
          Be aware that some things are public by design. Contributions are publicly attributed, and if
          you opt in, your name appears on the Wall of Fame or the trivia leaderboard. Everything else
          about your account stays private.
        </p>
        <p>
          The historical records in the database — musicians, bands, recordings, gigs and venues — are
          research material about public figures and public events, not personal information collected
          from site visitors. If you are featured in the database and believe something is wrong or
          should not be there, please get in touch and we will look into it.
        </p>
      </section>

      <section>
        <h2>Your rights</h2>
        <p>You can ask us to:</p>
        <ul>
          <li>tell you what personal information we hold about you;</li>
          <li>correct anything inaccurate;</li>
          <li>delete your account and personal information — see <Link href="/data-deletion">Data Deletion</Link>;</li>
          <li>remove your name from the Wall of Fame or the leaderboard.</li>
        </ul>
        <p>
          Email <a href={`mailto:${contact_email}`}>{contact_email}</a> and we will respond within a
          reasonable time. If you are unhappy with how we have handled a privacy matter, you can escalate
          it to the Office of the Australian Information Commissioner.
        </p>
      </section>

      <section>
        <h2>Retention and security</h2>
        <p>
          Account information is kept while your account is open, and removed when you ask us to delete
          it. Donation records are kept longer where we need them for financial and tax purposes. Access
          to the database is restricted to the site's administrators.
        </p>
      </section>

      <section>
        <h2>Children</h2>
        <p>
          The site is not directed at children under 13, and we do not knowingly collect their personal
          information. If you believe a child has created an account, contact us and we will remove it.
        </p>
      </section>

      <section>
        <h2>Changes</h2>
        <p>
          If this policy changes materially, the "last updated" date above will change with it. Please
          check back occasionally.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Questions about this policy or your information:{" "}
          <a href={`mailto:${contact_email}`}>{contact_email}</a>.
        </p>
      </section>
    </LegalPage>
  )
}
